// ==================== Proof-of-Work Solver ====================
// Anti-scraping defense: the backend may challenge a client with a
// proof-of-work puzzle (HTTP 429 + { pow: { token, difficulty, algorithm } }).
// The client must find the smallest non-negative integer nonce such that
// SHA-256(`${token}:${nonce}`) has at least `difficulty` leading zero BITS.
//
// The SHA-256 below is a minimal synchronous implementation (public-domain
// style, FIPS 180-4). We deliberately do NOT use crypto.subtle here — its
// per-call async overhead is far too slow for ~1M candidate hashes.

/* eslint-disable no-bitwise */

// SHA-256 round constants (first 32 bits of the fractional parts of the cube
// roots of the first 64 primes)
const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

// Initial hash values (first 32 bits of the fractional parts of the square
// roots of the first 8 primes)
const H_INIT = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
]);

// Reusable message schedule — solver is synchronous & single-threaded
const W = new Uint32Array(64);

/**
 * Synchronous SHA-256 of a byte array. Returns the 32-byte digest.
 */
export function sha256(message: Uint8Array): Uint8Array {
    const len = message.length;
    // Padded length: message + 0x80 + zero-pad + 8-byte big-endian bit length,
    // rounded up to a multiple of 64.
    const paddedLen = (((len + 8) >> 6) + 1) << 6;
    const padded = new Uint8Array(paddedLen);
    padded.set(message);
    padded[len] = 0x80;
    const dv = new DataView(padded.buffer);
    const bitLen = len * 8; // safe: JS numbers exact to 2^53
    dv.setUint32(paddedLen - 8, Math.floor(bitLen / 0x100000000), false);
    dv.setUint32(paddedLen - 4, bitLen >>> 0, false);

    const h = new Uint32Array(H_INIT);

    for (let offset = 0; offset < paddedLen; offset += 64) {
        for (let i = 0; i < 16; i++) {
            W[i] = dv.getUint32(offset + (i << 2), false);
        }
        for (let i = 16; i < 64; i++) {
            const x = W[i - 15];
            const y = W[i - 2];
            const s0 = (((x >>> 7) | (x << 25)) ^ ((x >>> 18) | (x << 14)) ^ (x >>> 3)) >>> 0;
            const s1 = (((y >>> 17) | (y << 15)) ^ ((y >>> 19) | (y << 13)) ^ (y >>> 10)) >>> 0;
            W[i] = (W[i - 16] + s0 + W[i - 7] + s1) >>> 0;
        }

        let a = h[0], b = h[1], c = h[2], d = h[3];
        let e = h[4], f = h[5], g = h[6], hh = h[7];

        for (let i = 0; i < 64; i++) {
            const S1 = (((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7))) >>> 0;
            const ch = ((e & f) ^ (~e & g)) >>> 0;
            const t1 = (hh + S1 + ch + K[i] + W[i]) >>> 0;
            const S0 = (((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10))) >>> 0;
            const maj = ((a & b) ^ (a & c) ^ (b & c)) >>> 0;
            const t2 = (S0 + maj) >>> 0;
            hh = g; g = f; f = e;
            e = (d + t1) >>> 0;
            d = c; c = b; b = a;
            a = (t1 + t2) >>> 0;
        }

        h[0] = (h[0] + a) >>> 0;
        h[1] = (h[1] + b) >>> 0;
        h[2] = (h[2] + c) >>> 0;
        h[3] = (h[3] + d) >>> 0;
        h[4] = (h[4] + e) >>> 0;
        h[5] = (h[5] + f) >>> 0;
        h[6] = (h[6] + g) >>> 0;
        h[7] = (h[7] + hh) >>> 0;
    }

    const out = new Uint8Array(32);
    const outView = new DataView(out.buffer);
    for (let i = 0; i < 8; i++) {
        outView.setUint32(i << 2, h[i], false);
    }
    return out;
}

/**
 * Number of leading zero bits in a digest.
 */
export function leadingZeroBits(digest: Uint8Array): number {
    let bits = 0;
    for (let i = 0; i < digest.length; i++) {
        const byte = digest[i];
        if (byte === 0) {
            bits += 8;
            continue;
        }
        bits += Math.clz32(byte) - 24; // clz32 counts within 32 bits; byte occupies the low 8
        break;
    }
    return bits;
}

// Solve in chunks so the UI thread isn't frozen; hard cap so we never spin forever.
const SOLVE_CHUNK_SIZE = 20000;
const SOLVE_MAX_MILLIS = 30000;

/**
 * Find the smallest non-negative integer nonce (returned as a decimal string)
 * such that SHA-256(`${token}:${nonce}`) has at least `difficulty` leading
 * zero bits. Yields to the event loop every SOLVE_CHUNK_SIZE candidates.
 * Rejects if no solution is found within SOLVE_MAX_MILLIS.
 */
export async function solvePow(token: string, difficulty: number): Promise<string> {
    if (!Number.isFinite(difficulty) || difficulty < 0 || difficulty > 256) {
        throw new Error(`Invalid proof-of-work difficulty: ${difficulty}`);
    }

    const prefix = new TextEncoder().encode(`${token}:`);
    // Reusable buffer: prefix + up to 20 ASCII digits of the nonce
    const buf = new Uint8Array(prefix.length + 20);
    buf.set(prefix);

    const started = Date.now();
    let nonce = 0;

    for (;;) {
        const chunkEnd = nonce + SOLVE_CHUNK_SIZE;
        for (; nonce < chunkEnd; nonce++) {
            const digits = String(nonce);
            let p = prefix.length;
            for (let i = 0; i < digits.length; i++) {
                buf[p++] = digits.charCodeAt(i);
            }
            const digest = sha256(buf.subarray(0, p));
            if (leadingZeroBits(digest) >= difficulty) {
                return String(nonce);
            }
        }
        if (Date.now() - started > SOLVE_MAX_MILLIS) {
            throw new Error(`Proof-of-work unsolved after ${SOLVE_MAX_MILLIS}ms (difficulty ${difficulty})`);
        }
        // Yield to the event loop so the UI doesn't freeze
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
}
